import { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import clinicApi from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";

function toLocalDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function BookAppointment() {
  const { affiliationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef(null);
  const ctx = location.state || {};

  const [step, setStep] = useState("pick"); // "pick" | "review"
  const [date, setDate] = useState(new Date());
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // ISO string (slot) or start_time (token)
  const [booking, setBooking] = useState(false);
  const [notes, setNotes] = useState("");

  const [tokenPreview, setTokenPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [forSelf, setForSelf] = useState(true);
  const [otherPatient, setOtherPatient] = useState({ first_name: "", last_name: "", phone_number: "", relationship: "" });

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    setSelected(null);
    clinicApi.get(`doctors/${affiliationId}/availability/`, { params: { date: toLocalDateStr(date) } })
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability(null))
      .finally(() => setLoading(false));
  }, [affiliationId, date]);

  const goToReview = () => {
    if (!selected) return;
    setStep("review");
    if (availability.mode === "token") {
      setPreviewLoading(true);
      clinicApi.get(`doctors/${affiliationId}/token-preview/`, { params: { date: toLocalDateStr(date), session_start_time: selected } })
        .then(res => setTokenPreview(res.data))
        .catch(() => setTokenPreview(null))
        .finally(() => setPreviewLoading(false));
    }
  };

  const handleBook = () => {
    if (!selected) return;
    if (!forSelf && (!otherPatient.first_name.trim() || !otherPatient.phone_number.trim())) {
      toast.current?.show({ severity: "warn", summary: "Missing details", detail: "Name and phone are required.", life: 3000 });
      return;
    }
    setBooking(true);
    const payload = availability.mode === "token"
      ? { affiliation_id: affiliationId, date: toLocalDateStr(date), session_start_time: selected, notes }
      : { affiliation_id: affiliationId, scheduled_at: selected, notes };
    if (!forSelf) {
      payload.patient_details = {
        first_name: otherPatient.first_name.trim(),
        last_name: otherPatient.last_name.trim(),
        phone_number: otherPatient.phone_number.trim(),
      };
    }

    clinicApi.post("appointments/", payload)
      .then(() => {
        toast.current?.show({ severity: "success", summary: "Booked", detail: "Appointment confirmed.", life: 2500 });
        setTimeout(() => navigate("/appointments"), 900);
      })
      .catch(err => {
        const data = err.response?.data || {};
        const detail = data.clinic_contact_phone
          ? `${data.error} Clinic contact: ${data.clinic_contact_phone}`
          : (data.error || "Please try another slot.");
        toast.current?.show({ severity: "error", summary: "Could not book", detail, life: 6000 });
      })
      .finally(() => setBooking(false));
  };

  // Token mode: show the session's actual START time to the patient, not
  // the clinic's internal "from-to" range label — the patient is booking
  // into a session that begins at this time, the end time of the whole
  // session isn't meaningful to them.
  const selectedLabel = availability?.mode === "token"
    ? selected
    : selected && new Date(selected).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const consultationFee = ctx.fee ? parseFloat(ctx.fee) : 0;
  const facilityFeeAmount = ctx.facilityFeeAmount ? parseFloat(ctx.facilityFeeAmount) : 0;
  const totalFee = consultationFee + facilityFeeAmount;
  const fmt = (v) => `Rs. ${v.toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;

  return (
    <PortalLayout>
      <Toast ref={toast} />

      <div className="page-header">
        <h1 className="page-title">Book Appointment</h1>
        <p className="page-subtitle">
          {ctx.doctorName ? `Dr. ${ctx.doctorName}` : "Doctor"}{ctx.specialty ? ` (${ctx.specialty})` : ""}
          {ctx.clinicName ? ` · ${ctx.clinicName}` : ""}
        </p>
      </div>

      {step === "pick" && (
        <>
          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8 }}>Select a date</div>
            <Calendar value={date} onChange={(e) => setDate(e.value)} minDate={new Date()} inline showWeek={false} />
          </div>

          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10 }}>
              {availability?.mode === "token" ? "Select a session" : "Select a time"}
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-muted)" }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: 22 }} />
              </div>
            ) : availability?.mode === "token" ? (
              availability.sessions.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No sessions available on this date.</div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {availability.sessions.map((s) => (
                    <button
                      key={s.start_time}
                      onClick={() => setSelected(s.start_time)}
                      disabled={s.booked_count >= s.capacity}
                      style={{
                        padding: "10px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                        border: `1.5px solid ${selected === s.start_time ? "var(--brand-primary)" : "var(--border-color)"}`,
                        background: selected === s.start_time ? "var(--brand-primary)" : "transparent",
                        color: selected === s.start_time ? "white" : "var(--text-primary)",
                        cursor: "pointer", opacity: s.booked_count >= s.capacity ? 0.4 : 1,
                      }}
                    >
                      {s.start_time}
                    </button>
                  ))}
                </div>
              )
            ) : availability?.mode === "slot" ? (
              availability.slots.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No slots available on this date.</div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {availability.slots.map((s) => {
                    const t = new Date(s.start).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                    return (
                      <button
                        key={s.start}
                        onClick={() => setSelected(s.start)}
                        style={{
                          padding: "10px 14px", borderRadius: 10, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                          border: `1.5px solid ${selected === s.start ? "var(--brand-primary)" : "var(--border-color)"}`,
                          background: selected === s.start ? "var(--brand-primary)" : "transparent",
                          color: selected === s.start ? "white" : "var(--text-primary)",
                          cursor: "pointer",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Pick a date to see availability.</div>
            )}
          </div>

          <button
            onClick={goToReview}
            disabled={!selected}
            style={{
              width: "100%", padding: "12px", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
              background: "var(--brand-primary)", color: "white", border: "none",
              cursor: !selected ? "not-allowed" : "pointer", opacity: !selected ? 0.6 : 1,
            }}
          >
            Continue
          </button>
        </>
      )}

      {step === "review" && (
        <>
          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Appointment Details
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13.5 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>Doctor</span>
                <span style={{ fontWeight: 600 }}>
                  {ctx.doctorName ? `Dr. ${ctx.doctorName}` : "—"}{ctx.specialty ? ` (${ctx.specialty})` : ""}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>Clinic</span>
                <span style={{ fontWeight: 600 }}>{ctx.clinicName || "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>Date</span>
                <span style={{ fontWeight: 600 }}>{date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>{availability?.mode === "token" ? "Session" : "Time"}</span>
                <span style={{ fontWeight: 600 }}>{selectedLabel}</span>
              </div>
              {availability?.mode === "token" && (
                previewLoading ? (
                  <div style={{ textAlign: "center", padding: "10px 0" }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: 16, color: "var(--brand-primary)" }} />
                  </div>
                ) : tokenPreview && (
                  <div style={{
                    marginTop: 6, padding: "10px 12px", background: "var(--brand-light)", borderRadius: 8,
                    display: "flex", flexDirection: "column", gap: 4,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--brand-primary)" }}>Your Token Number</span>
                      <span style={{ fontWeight: 700, color: "var(--brand-primary)" }}>#{tokenPreview.next_token}</span>
                    </div>
                    {tokenPreview.estimated_time && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                        <span style={{ color: "var(--text-muted)" }}>Estimated Time</span>
                        <span>{tokenPreview.estimated_time} (approximate)</span>
                      </div>
                    )}
                    {tokenPreview.is_full && (
                      <div style={{ fontSize: 12, color: "#b45309", marginTop: 2 }}>
                        This session is at capacity — booking may be refused.
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Payment Details
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13.5 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>Consultation Fee</span>
                <span style={{ fontWeight: 600 }}>{fmt(consultationFee)}</span>
              </div>
              {facilityFeeAmount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--text-muted)" }}>{ctx.facilityFeeLabel || "Hospital / Facility Fee"}</span>
                  <span style={{ fontWeight: 600 }}>{fmt(facilityFeeAmount)}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid var(--border-color)" }}>
                <span style={{ fontWeight: 700 }}>Total</span>
                <span style={{ fontWeight: 700 }}>{fmt(totalFee)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-muted)" }}>Payment Status</span>
                <span style={{
                  fontWeight: 600, fontSize: 11.5, padding: "2px 9px", borderRadius: 20,
                  background: "#fef3c7", color: "#b45309",
                }}>
                  Pending — pay at clinic
                </span>
              </div>
            </div>
          </div>

          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 10 }}>Who is this appointment for?</div>
            <div style={{ display: "flex", gap: 8, marginBottom: forSelf ? 0 : 12 }}>
              {[{ key: true, label: "Myself" }, { key: false, label: "Someone else" }].map(({ key, label }) => (
                <button
                  key={label}
                  onClick={() => setForSelf(key)}
                  style={{
                    flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                    border: `1.5px solid ${forSelf === key ? "var(--brand-primary)" : "var(--border-color)"}`,
                    background: forSelf === key ? "var(--brand-primary)" : "transparent",
                    color: forSelf === key ? "white" : "var(--text-primary)", cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {!forSelf && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    placeholder="First name"
                    value={otherPatient.first_name}
                    onChange={(e) => setOtherPatient({ ...otherPatient, first_name: e.target.value })}
                    style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
                  />
                  <input
                    placeholder="Last name"
                    value={otherPatient.last_name}
                    onChange={(e) => setOtherPatient({ ...otherPatient, last_name: e.target.value })}
                    style={{ flex: 1, padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
                  />
                </div>
                <input
                  placeholder="Phone number"
                  value={otherPatient.phone_number}
                  onChange={(e) => setOtherPatient({ ...otherPatient, phone_number: e.target.value })}
                  style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
                />
                <input
                  placeholder="Relationship (e.g. spouse, child, parent) — optional"
                  value={otherPatient.relationship}
                  onChange={(e) => setOtherPatient({ ...otherPatient, relationship: e.target.value })}
                  style={{ padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit" }}
                />
              </div>
            )}
          </div>

          <div className="ph-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8 }}>Notes (optional)</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Reason for visit, symptoms, etc."
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--border-color)", fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
            />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setStep("pick")}
              style={{
                padding: "12px 20px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "inherit",
                background: "var(--surface-bg)", color: "var(--text-secondary)", border: "1px solid var(--border-color)", cursor: "pointer",
              }}
            >
              Back
            </button>
            <button
              onClick={handleBook}
              disabled={booking}
              style={{
                flex: 1, padding: "12px", borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
                background: "var(--brand-primary)", color: "white", border: "none",
                cursor: booking ? "not-allowed" : "pointer", opacity: booking ? 0.6 : 1,
              }}
            >
              {booking ? "Booking…" : "Confirm Booking"}
            </button>
          </div>
        </>
      )}
    </PortalLayout>
  );
}
